import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Button,
  FormFeedback,
  Spinner,
} from "reactstrap";
import Usermgmt from "../../../../services/UserServices/Usermgmt";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

const Userform = ({ isOpen, toggle, id, selectedItem, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [passwordShow, setPasswordShow] = useState(false);
  const [details, setDetails] = useState(null);

  // FETCH DETAIL FOR EDIT MODE
  const fetchDetail = async () => {
    if (!id) return; // Only fetch if id exists

    try {
      const response = await Usermgmt.getuserbyid(id); // Added await
      setDetails(response);
    } catch (error) {
      console.error("Error fetching user:", error);
      toast.error("Failed to fetch user details");
    }
  };

  useEffect(() => {
    if (id && isOpen) {
      fetchDetail();
    } else {
      setDetails(null); // Reset details when creating new user
    }
  }, [id, isOpen]);

  // FORMIK SETUP
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: details?.username || selectedItem?.username || "",
      email: details?.email || selectedItem?.email || "",
      password: "",
      mobile: details?.mobile
        ? String(details.mobile)
        : selectedItem?.mobile
        ? String(selectedItem.mobile)
        : "",
      status: details?.status ?? selectedItem?.status ?? true,
      roles: details?.roles || selectedItem?.roles || ["User"],
    },

    validationSchema: Yup.object({
      username: Yup.string()
        .required("Username is required")
        .min(2, "Username must be at least 2 characters")
        .max(50, "Username must not exceed 50 characters"),

      mobile: Yup.string()
        .required("Phone number is required")
        .matches(/^[0-9]+$/, "Phone number must be digits only")
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number must be exactly 10 digits"),

      email: Yup.string()
        .email("Invalid email address")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        )
        .required("Email is required"),

      password: id
        ? Yup.string().notRequired() // Not required for edit
        : Yup.string()
            .min(6, "Password must be at least 6 characters")
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
              "Password must contain at least one uppercase, one lowercase, and one number"
            )
            .required("Password is required"),

      status: Yup.boolean().required("Status is required"),
      roles: Yup.array().min(1, "At least one role is required"),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);

        // Build payload
        const payload = {
          username: values.username,
          email: values.email,
          mobile: String(values.mobile),
          status: values.status,
          roles: values.roles,
        };

        // Add password only if creating new user OR if password is provided in edit mode
        if (!id) {
          payload.password = values.password;
        } else if (values.password) {
          payload.password = values.password; // Only update password if provided
        }

        console.log("Payload:", payload);

        if (id) {
          // UPDATE
          await Usermgmt.update(id, payload);
          toast.success("User updated successfully");
        } else {
          // CREATE
          await Usermgmt.create(payload);
          toast.success("User created successfully");
          resetForm();
        }

        // Call success callback to refresh list
        if (onSuccess) {
          onSuccess();
        }

        // Close the offcanvas
        toggle();
      } catch (error) {
        console.error("Submit Error:", error);
        
        // Handle specific errors
        if (error.response?.status === 400) {
          toast.error(error.response.data?.message || "Invalid data");
        } else if (error.response?.status === 409) {
          toast.error("User with this email already exists");
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Something went wrong!");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      backdrop={true}
      style={{ width: "420px" }}
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom">
        <i className="ri-user-line me-2"></i>
        {id ? "Edit User" : "Add User"}
      </OffcanvasHeader>

      <Form
        onSubmit={formik.handleSubmit}
        className="d-flex flex-column"
        style={{ height: "calc(100vh - 73px)" }}
      >
        {/* SCROLLABLE CONTENT */}
        <div className="flex-grow-1 overflow-auto p-3">
          {/* Username */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">
              Username <span className="text-danger">*</span>
            </Label>
            <Input
              id="username"
              name="username"
              type="text"
              className="form-control shadow-sm"
              placeholder="Enter username"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.username}
              invalid={!!(formik.touched.username && formik.errors.username)}
              disabled={loading}
            />
            {formik.touched.username && formik.errors.username && (
              <FormFeedback type="invalid">
                {formik.errors.username}
              </FormFeedback>
            )}
          </div>

          {/* Email */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">
              Email <span className="text-danger">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              className="form-control shadow-sm"
              placeholder="Enter email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              invalid={!!(formik.touched.email && formik.errors.email)}
              disabled={loading}
            />
            {formik.touched.email && formik.errors.email && (
              <FormFeedback type="invalid">
                {formik.errors.email}
              </FormFeedback>
            )}
          </div>

          {/* Phone Number */}
          <div className="mb-3">
            <Label className="fw-bold mb-2">
              Phone Number <span className="text-danger">*</span>
            </Label>
            <Input
              id="mobile"
              name="mobile"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control shadow-sm"
              placeholder="Enter phone number"
              value={formik.values.mobile}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/\D/g, "");
                formik.setFieldValue("mobile", onlyNums);
              }}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.mobile && formik.errors.mobile)}
              disabled={loading}
            />
            {formik.touched.mobile && formik.errors.mobile && (
              <FormFeedback type="invalid">
                {formik.errors.mobile}
              </FormFeedback>
            )}
          </div>

          {/* Password */}
          <div className="mb-3 position-relative">
            <Label className="fw-bold mb-2">
              Password {!id && <span className="text-danger">*</span>}
              {id && <small className="text-muted ms-1">(Leave blank to keep current)</small>}
            </Label>
            <Input
              id="password"
              name="password"
              type={passwordShow ? "text" : "password"}
              className="form-control shadow-sm pe-5"
              placeholder={id ? "Enter new password (optional)" : "Enter password"}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              invalid={!!(formik.touched.password && formik.errors.password)}
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setPasswordShow(!passwordShow)}
              className="btn btn-link position-absolute end-0 text-decoration-none text-muted"
              style={{ top: "32px", zIndex: 10 }}
              disabled={loading}
            >
              {passwordShow ? (
                <i className="bx bxs-show"></i>
              ) : (
                <i className="bx bxs-hide"></i>
              )}
            </button>
            {formik.touched.password && formik.errors.password && (
              <FormFeedback type="invalid">
                {formik.errors.password}
              </FormFeedback>
            )}
          </div>

          {/* Status Switch */}
          <FormGroup switch className="mt-3">
            <Input
              type="switch"
              role="switch"
              name="status"
              checked={formik.values.status}
              onChange={(e) => formik.setFieldValue("status", e.target.checked)}
              disabled={loading}
            />
            <Label check className="fw-medium">
              Status: {formik.values.status ? "Active" : "Inactive"}
            </Label>
          </FormGroup>

          {/* Roles Selection */}
          <div className="mb-3 mt-4">
            <Label className="fw-bold mb-2">
              User Roles <span className="text-danger">*</span>
            </Label>
            <div className="border rounded p-3" style={{ backgroundColor: "#f8f9fa" }}>
              <FormGroup check className="mb-2">
                <Input
                  id="roleUser"
                  name="roleUser"
                  type="checkbox"
                  checked={formik.values.roles?.includes("User") || false}
                  onChange={(e) => {
                    const newRoles = e.target.checked
                      ? [...(formik.values.roles || []), "User"]
                      : (formik.values.roles || []).filter(role => role !== "User");
                    formik.setFieldValue("roles", newRoles);
                  }}
                  disabled={loading}
                />
                <Label check htmlFor="roleUser" className="ms-2">
                  User (Access to Ads Management Module)
                </Label>
              </FormGroup>
              <FormGroup check className="mb-2">
                <Input
                  id="roleAdmin"
                  name="roleAdmin"
                  type="checkbox"
                  checked={formik.values.roles?.includes("Admin") || false}
                  onChange={(e) => {
                    const newRoles = e.target.checked
                      ? [...(formik.values.roles || []), "Admin"]
                      : (formik.values.roles || []).filter(role => role !== "Admin");
                    formik.setFieldValue("roles", newRoles);
                  }}
                  disabled={loading}
                />
                <Label check htmlFor="roleAdmin" className="ms-2">
                  Admin (Full Access)
                </Label>
              </FormGroup>
            </div>
            {formik.touched.roles && formik.errors.roles && (
              <small className="text-danger d-block mt-2">
                {formik.errors.roles}
              </small>
            )}
          </div>
        </div>

        {/* FIXED FOOTER BUTTONS */}
        <div className="bg-white border-top p-3 d-flex justify-content-between gap-2">
          <Button
            color="danger"
            onClick={toggle}
            className="w-50 shadow-sm border"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="success"
            type="submit"
            disabled={loading}
            className="w-50 shadow-sm"
          >
            {loading && <Spinner size="sm" className="me-2" />}
            {loading ? (id ? "Updating..." : "Creating...") : id ? "Update" : "Create"}
          </Button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default Userform;